/* ------------------------------------------------------------------
   qr.js  —  QR codes for the Link in Bio page (Part E, T32)
   Made in the browser with the `qrcode` package (no server call).
   One matrix feeds the on-screen code, the PNG and the print SVG, so all
   three are the same code. Error correction 'M' (15%) survives a little
   wear on a flyer or sign; a 4-module quiet zone, as the QR spec asks.
------------------------------------------------------------------ */
import QRCode from 'qrcode';

export const QUIET_ZONE = 4;

/** { size, isDark(row, col) } for `text`. */
export function qrMatrix(text) {
  const { modules } = QRCode.create(String(text), { errorCorrectionLevel: 'M' });
  const { size, data } = modules;
  return { size, isDark: (r, c) => !!data[r * size + c] };
}

/** One SVG path for every dark module (runs on a row are merged). */
export function qrPath(matrix, offset = 0) {
  let d = '';
  for (let r = 0; r < matrix.size; r++) {
    for (let c = 0; c < matrix.size; c++) {
      if (!matrix.isDark(r, c)) continue;
      let run = 1;
      while (c + run < matrix.size && matrix.isDark(r, c + run)) run++;
      d += `M${c + offset} ${r + offset}h${run}v1h-${run}z`;
      c += run - 1;
    }
  }
  return d;
}

/** A standalone SVG file (black on white, with the quiet zone) for print. */
export function qrSvgString(text) {
  const m = qrMatrix(text);
  const n = m.size + QUIET_ZONE * 2;
  return `<?xml version="1.0" encoding="UTF-8"?>\n`
    + `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" width="${n * 10}" height="${n * 10}" shape-rendering="crispEdges">`
    + `<title>${escapeXml(text)}</title>`
    + `<rect width="${n}" height="${n}" fill="#ffffff"/>`
    + `<path fill="#000000" d="${qrPath(m, QUIET_ZONE)}"/>`
    + `</svg>\n`;
}

/** A PNG (about 1024 px square) as a Blob, drawn on a canvas. */
export function qrPngBlob(text, px = 1024) {
  const m = qrMatrix(text);
  const n = m.size + QUIET_ZONE * 2;
  const scale = Math.max(1, Math.floor(px / n));
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = n * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas_unavailable'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  for (let r = 0; r < m.size; r++) {
    for (let c = 0; c < m.size; c++) {
      if (m.isDark(r, c)) ctx.fillRect((c + QUIET_ZONE) * scale, (r + QUIET_ZONE) * scale, scale, scale);
    }
  }
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('png_failed'))), 'image/png'));
}

/** Save a Blob as a file through a temporary link. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function escapeXml(s) {
  return String(s).replace(/[<>&"']/g, ch => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[ch]));
}
